import { Box, useTheme } from "@mui/material";
import { useEffect, useRef } from "react";

/**
 * Sticky offset for the toolbar. When the tab bar is rendered above it, the
 * toolbar docks just beneath it; otherwise (e.g. mobile with a single tab,
 * where the tab bar collapses to nothing) it sticks to the very top so it
 * doesn't leave a gap.
 */
export const getStickyTop = (
  hasTabs: boolean,
): number | { xs: string; md: string } =>
  hasTabs ? { xs: "36px", md: "72px" } : 0;

export const StickyToolbarWrapper: React.FC<{
  children: React.ReactNode;
  hasTabs?: boolean;
}> = ({ children, hasTabs = true }) => {
  const theme = useTheme();

  const filterBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollContainer = document.querySelector("main");
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (filterBoxRef.current) {
        filterBoxRef.current.style.boxShadow =
          scrollContainer.scrollTop > 0
            ? "0px 4px 8px -2px rgba(0, 0, 0, 0.1)"
            : "none";
      }
    };

    handleScroll(); // apply immediately in case container is already scrolled

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box
      ref={filterBoxRef}
      sx={{
        position: "sticky",
        top: getStickyTop(hasTabs),
        height: { xs: "fit-content", md: "92px" },
        paddingTop: 3,
        paddingBottom: 1,
        zIndex: 9,
        backgroundColor: theme.colors.defaultBackground,
        marginLeft: -3,
        marginRight: -3,
        px: 3,
      }}
    >
      {children}
    </Box>
  );
};
